import { IconCheckupList, IconUser } from "@tabler/icons-react";
import React, { useState, useEffect } from "react";
import styles from "./StaffAssessments.module.css"
import { Typography, Col, Row, Button, Tooltip, message, Card, Select, Input, Table, Modal, Spin } from "antd";
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../../authentication/axiosInstance';
import moment from 'moment-timezone';

const { Title, Text } = Typography;
const { Option } = Select;

export default function StaffAssessments() {

    const date = moment()

    const [loading, setLoading] = useState(false);
    const [evaluations, setEvaluations] = useState([])
    const [loadingSync, setLoadingSync] = useState(false);

    const [jobTypeFilter, setJobTypeFilter] = useState('all');
    const [assessmentTypeFilter, setAssessmentTypeFilter] = useState('post');
    const [staffNameFilter, setStaffNameFilter] = useState("");

    useEffect(() => {
        fetchEvaluations();
    }, [jobTypeFilter, assessmentTypeFilter, staffNameFilter]);

    const fetchEvaluations = async () => {
        setLoading(true)
        try {
            const response = await api.get('/jotform-request/assessments/submissions/', {
                params: {
                    position: jobTypeFilter !== 'all' ? jobTypeFilter : undefined,
                    assessmentType: assessmentTypeFilter !== 'all' ? assessmentTypeFilter : undefined,
                    staffName: staffNameFilter !== "" ? staffNameFilter : undefined
                }
            });
            setEvaluations(response.data);
        } catch (error) {
            message.error('Failed to fetch staff evaluations')
        } finally {
            setLoading(false);
        }
    }

    const syncAssessments = async () => {

        setLoadingSync(true);
        try {
            await api.post('/jotform-request/assessments/sync_staff_assessments/'
            ).then(() => {
                fetchEvaluations()
                message.success('Jotform staff assessments sync completed successfully!');
            });
        } catch {
            message.error('Failed to sync staff evaluations')
        } finally {
            setLoadingSync(false)
        }
    }

    const downloadCSV = () => {

        const headers = [
            'Staff', 'Job Type', 'Assessment Type', 'Date Completed', 
            'Total Score', 'Percentage Score', 'Final Rating'
        ];

        const csvContent = [
            headers.join(','),
            ...evaluations.map(form => {
            return [
                form.staff.first_name + " " + form.staff.last_name,
                form.position,
                form.assessment_type,
                moment(form.date_completed).format('YYYY-MM-DD'),
                form.total_score,
                form.percentage_score,
                form.final_rating,
            ].join(',');
            })
        ].join('\n');
        
        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);

        link.setAttribute('download', `staff_assessments_data.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        message.success('Data export successful!');
    }

    const downloadPDF = async (url, record) => {
        try {
            await api.get(`/jotform-request/assessments/form_pdf/`, {
                params: { url },
                responseType: 'arraybuffer',
            }).then((response) => {                
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const pdfURL = window.URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = pdfURL;
                link.setAttribute('download', `${record.staff.name.split(" ").join("_")}_${moment(record.date_completed).format('YYYY-MM-DD')}_full_report.pdf`);
        
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            message.success("Staff assessment PDF generated successfully");
        });
        } catch (error) {
            console.log(error)
            message.error("Failed to generate staff assessment PDF");
        }
    }

    const columns = [
        {
            title: "Staff",
            key: "staff",
            fixed: "left",
            width: 180,
            render: (value,record) => ( 
                <div className={styles.rowData}>
                    <IconUser size={14} />
                    {record.staff.name}
                </div>
            )       
        }, 
        {
            title: "Job Type",
            key: "jobType",
            dataIndex: "position",
            width: 150,   
            render: position => (
                <span>{position.charAt(0).toUpperCase()}{position.slice(1)}</span>
            )
        }, 
        {
            title: "Assessment Type",
            key: "assessmentType",
            dataIndex: "assessment_type",
            width: 150,   
            render: type => (
                <span>{type.charAt(0).toUpperCase()}{type.slice(1)}</span>
            )
        }, 
        {
            title: "Date Completed",
            dataIndex: "date_completed",
            key: "dateCompleted",
            width: 150,
            render: (date) => <span>{moment(date).format("MMM DD, YYYY")}</span>,
            sorter: (a, b) => moment(a.date_completed).valueOf() - moment(b.date_completed).valueOf(),
        },
        {
            title: 'Total Score',
            dataIndex: 'total_score',
            key: 'totalScore',
            width: 150,
        },
        {
            title: 'Percentage Score',
            dataIndex: 'percentage_score',
            key: 'percentageScore',
            width: 150,
        },
        {
            title: 'Final Rating',
            dataIndex: 'final_rating',
            key: 'finalRating',
            width: 150,
            render: finalRating => {
                return {
                props: {
                    style: { background: finalRating >= 4 ? "#bce5aa": finalRating >= 3 ? "#f5d682" : finalRating >= 2 ? "#faaf7a" : "#FF9C9C"}
                },
                children: 
                    <span>{finalRating === 'n/a' ? finalRating : `${finalRating}/5`}</span>
                };
            }
        },
        {
            title: 'Download Full Report',
            dataIndex: 'url',
            key: 'pdf',
            width: 150,
            render: (url, record) => (
                <Button onClick={() => downloadPDF(url, record)}>
                    Full Report
                </Button>
            )
        },
    ]

    return (
    <div className={styles.container}>
        {/* Syncing Modal */}
        <Modal
            open={loadingSync}
            title={null}
            footer={null}
            centered
            closable={false}
            maskClosable={false}
            bodyStyle={{ textAlign: 'center', padding: 40 }}
        >
            <Spin size="large" style={{ marginBottom: 20 }} />
            <h2>Syncing Jotform Staff Assessment</h2>
            <p>Please wait while we update your information...</p>
        </Modal>
            
        {/* Header */}
        <div className={styles.header}>
            <Row align="middle" justify="space-between">
                <Col>
                    <Title level={2} className={styles.pageTitle}>
                    <IconCheckupList /> Staff Assessments
                    </Title>
                    <Text className={styles.subheading}>
                    View staff evaluations
                    </Text>
                </Col>
                <Col>
                    <Tooltip title="Sync Jotform Assessments">
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={syncAssessments}
                        loading={loading}
                        className={styles.refreshButton}
                    >
                        Sync Jotform
                    </Button>
                    </Tooltip>
                </Col>
            </Row>
        </div>

        {/* Filters */}
        <Card className={styles.filterCard}>
            <Row gutter={[16, 16]} align="middle">
                <Col xs={12} md={6} lg={3}>
                    <Text strong className={styles.filterLabel}>Job Type:</Text>
                    <Select
                        value={jobTypeFilter}
                        onChange={setJobTypeFilter}
                        className={styles.typeSelect}
                        disabled={loading}
                    >
                        <Option value="all">All</Option>
                        <Option value="walkway">Walkway</Option>
                        <Option value="salting">Salting</Option>
                        <Option value="loader">Loader</Option>
                    </Select>
                </Col>

                <Col xs={12} md={6} lg={3}>
                    <Text strong className={styles.filterLabel}>Assessment Type:</Text>
                    <Select
                        value={assessmentTypeFilter}
                        onChange={setAssessmentTypeFilter}
                        className={styles.typeSelect}
                        disabled={loading}
                    >
                        <Option value="all">All</Option>
                        <Option value="pre">Pre Season</Option>
                        <Option value="mid">Mid Season</Option>
                        <Option value="post">Post Season</Option>
                    </Select>
                </Col>
                
                <Col xs={24} md={8} lg={6}>
                    <Text strong className={styles.filterLabel}>Staff Name:</Text>
                    <Input
                        placeholder="Filter by staff name"
                        value={staffNameFilter}
                        onChange={e => setStaffNameFilter(e.target.value)}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                </Col>

            </Row>
        </Card>

        {/* Evaluations Table */}
        <Card className={styles.tableCard}>
            <div className={styles.tableActions}>
                <Tooltip title="Download as CSV">
                    <Button 
                    icon={<DownloadOutlined />} 
                    onClick={downloadCSV}
                    disabled={!evaluations.length || loading}
                    className={styles.downloadButton}
                    >
                    Export Data
                    </Button>
                </Tooltip>
            </div>
            <Table
                columns={columns}
                dataSource={evaluations}
                loading={loading}
                pagination={{ 
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records` 
                }}
                className={styles.dataTable}
                rowClassName={styles.tableRow}
                bordered={true}
                size="middle"
                scroll={{ x: "max-content" }}
            />
        </Card>
    </div>
    );
}